import { z } from 'zod';

/**
 * Schema for error counts per data sync stage
 */
export const dataSyncStageErrorsSchema = z.object({
  dataLoading: z.number().default(0),
  dataTransformation: z.number().default(0),
  dataValidation: z.number().default(0),
  init: z.number().default(0),
  syncExecution: z.number().default(0),
});

/**
 * Schema for logs per data sync stage
 */
export const dataSyncStageLogsSchema = z.object({
  dataLoading: z.array(z.string()).optional(),
  dataTransformation: z.array(z.string()).optional(),
  dataValidation: z.array(z.string()).optional(),
  init: z.array(z.string()).optional(),
  syncExecution: z.array(z.string()).optional(),
});

/**
 * Schema for sync log summary
 */
export const dataSyncLogSummarySchema = z.object({
  created: z.number().default(0),
  errors: dataSyncStageErrorsSchema.optional(),
  unchanged: z.number().default(0),
  updated: z.number().default(0),
});

/**
 * Schema for task log summary view (without logs/error_logs)
 */
export const dataSyncTaskLogSummarySchema = z.object({
  dataSyncJobId: z.uuid().optional().nullable(),
  dataSyncTaskId: z.uuid(),
  finishedOn: z.union([z.coerce.date(), z.null()]),
  id: z.uuid(),
  jobName: z.string().optional().nullable(),
  result: z
    .union([z.literal('success'), z.literal('error')])
    .nullable()
    .optional(),
  startedOn: z.union([z.coerce.date(), z.null()]),
  summary: dataSyncLogSummarySchema.optional().nullable(),
  taskName: z.string().optional().nullable(),
});

/**
 * Schema for task log detail view (with logs/error_logs)
 */
export const dataSyncTaskLogDetailSchema = dataSyncTaskLogSummarySchema.extend({
  dryRunResults: z.record(z.string(), z.unknown()).optional().nullable(),
  errorLogs: dataSyncStageLogsSchema.optional().nullable(),
  logs: dataSyncStageLogsSchema.optional().nullable(),
});

/**
 * Schema for paginated task log response
 */
export const paginatedDataSyncTaskLogResponseSchema = z.object({
  data: z.array(dataSyncTaskLogSummarySchema),
  hasNextPage: z.boolean(),
  limit: z.number().min(1).max(500).default(50).optional(),
  offset: z.number().min(0).default(0).optional(),
  totalCount: z.number(),
});

/**
 * Query parameters for fetching logs
 */
export const dataSyncLogQueryParamsSchema = z.object({
  limit: z.number().min(1).max(500).default(50).optional(),
  offset: z.number().min(0).default(0).optional(),
  orderBy: z.string().default('finishedOn').optional(),
  sortOrder: z.enum(['ASC', 'DESC']).default('DESC').optional(),
});
